/**
 * One action, and everything that is true about it.
 *
 * A super admin following something up needs three facts and should not have to hunt
 * for any of them: what was done, who did it, when. So they get their own labelled
 * rows rather than being merged into one line, and the card never leaves a blank where
 * a name is expected - if the person genuinely was not recorded, it says so and says
 * why. A blank looks like a bug; "not recorded" is information.
 *
 * Where the facts come from, in order:
 *   - status and the standing detail come from the deal record, which knew a booking
 *     had happened long before anything was being audited;
 *   - the time comes from the audit event if there is one, and otherwise from the
 *     record's own timestamp, so "when" is almost never unknown;
 *   - the person comes only from the audit event, because that is the only place it
 *     has ever been stored.
 *
 * The wording follows the GOV.UK task-list pattern, tested with 34 users: a tick on
 * its own confused people, "Completed" read as more authoritative than "Done", and
 * plain text beat coloured boxes.
 */
import { useId, useState } from 'react'
import type { ReactNode } from 'react'
import HoverDetail from './HoverDetail'
import { ActionEvent, shortWhen } from '../services/actionLog'
import './ActionCard.css'

export type ActionStatus =
  | 'not_started'
  | 'in_progress'
  | 'completed'
  | 'failed'
  | 'blocked'
  /* Does not apply to this deal at all. Not "later", not "yet" - never. */
  | 'not_required'

const STATUS_WORDS: Record<ActionStatus, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  completed: 'Completed',
  failed: 'Could not complete',
  blocked: 'Cannot start yet',
  not_required: 'Not required',
}

export interface Repeat {
  /** The event being repeated, where one was recorded. */
  of: string | null
  reason: string
}

export interface ActionCardProps {
  title: string
  hint: string
  /**
   * What the record itself says about this action. Comes from the deal, not from the
   * audit log, so a card is never blind to something the record already proves.
   */
  status?: ActionStatus
  /** One line of fact from the record, e.g. "Booked - inspection pending". */
  detail?: string | null
  /**
   * When it happened, according to the deal record. Used when no audit event exists,
   * which is every action taken before this screen started keeping track.
   */
  at?: string | null
  /** The matching audit event, which is the only place the person is ever stored. */
  last?: ActionEvent | null
  count?: number
  badge?: string | null
  /**
   * Why the action cannot be run. Shown on a blocked card, and on one that does not
   * apply at all; on a `not_required` card it also stands in for the detail line,
   * because greying something out does not on its own say why.
   */
  blockedReason?: string
  busy?: boolean
  /** False for the few things that cannot sensibly be done a second time at all. */
  repeatable?: boolean
  onRun: (repeat?: Repeat) => void

  /*
   * Anything this card needs to offer that is not the action itself - a link to
   * the document the action already produced, for instance. It renders under
   * the card, so reaching the document never means pressing the action and
   * setting it off a second time.
   */
  extra?: ReactNode
}

/** Enough to be a reason, not so much that it becomes an essay nobody writes. */
const MIN_REASON = 5

export default function ActionCard({
  title,
  hint,
  status,
  detail,
  at,
  last,
  count,
  badge,
  blockedReason,
  busy,
  repeatable = true,
  onRun,
  extra,
}: ActionCardProps) {
  const [asking, setAsking] = useState(false)
  const [reason, setReason] = useState('')
  const uid = useId()

  const done = status === 'completed'
  const failed = status === 'failed'
  const blocked = status === 'blocked'
  const locked = done && !repeatable
  const notRequired = status === 'not_required'
  const happened = done || failed || status === 'in_progress'

  /* The event is the better source when it exists, because it is the only one that
     also knows who. Otherwise fall back to whatever the record stamped. */
  const whenText = last ? shortWhen(last.executedAt) : at ? shortWhen(at) : null

  /* An event with no name against it is not proof that nothing human did it - the
     column simply did not exist until today. Say what is true, which is that the
     person was not recorded, rather than inventing an automation that may not have
     been one. */
  const whoText = last
    ? last.actorName || 'Not recorded against this event.'
    : 'Not recorded. This happened before the app began keeping track of who did what.'

  const click = () => {
    if (busy || blocked || notRequired || locked) return
    // A failed attempt is not a repeat - there is nothing to undo, it never happened.
    if (done) {
      setAsking(true)
      return
    }
    onRun()
  }

  const confirm = () => {
    const why = reason.trim()
    if (why.length < MIN_REASON) return
    setAsking(false)
    setReason('')
    onRun({ of: last ? last.id : null, reason: why })
  }

  const cancel = () => {
    setAsking(false)
    setReason('')
  }

  const popup = (
    <>
      <span className="hd-title">{title}</span>
      <span className="hd-row">{hint}</span>

      <span className="hd-sep" />

      {status ? (
        <span className="hd-row">
          <span className="hd-k">Status</span>
          {STATUS_WORDS[status]}
        </span>
      ) : null}

      {detail ? (
        <span className="hd-row">
          <span className="hd-k">Where it stands</span>
          {detail}
        </span>
      ) : null}

      {(blocked || notRequired) && blockedReason ? (
        <span className="hd-row hd-warn">
          <span className="hd-k">{notRequired ? 'Why not' : 'Why not yet'}</span>
          {blockedReason}
        </span>
      ) : null}

      {/* Who and when get their own rows, and appear together, because following
          something up needs both or neither is much use. */}
      {happened ? (
        <>
          <span className="hd-row">
            <span className="hd-k">When</span>
            {whenText || 'Not recorded'}
          </span>
          <span className={last && last.actorName ? 'hd-row' : 'hd-row hd-quiet'}>
            <span className="hd-k">Who</span>
            {whoText}
          </span>
        </>
      ) : null}

      {last && count && count > 1 ? (
        <span className="hd-row">
          <span className="hd-k">Times done</span>
          {count}
        </span>
      ) : null}

      {last && last.repeatReason ? (
        <span className="hd-row">
          <span className="hd-k">Repeated because</span>
          {last.repeatReason}
        </span>
      ) : null}

      {last && last.errorMessage ? (
        <span className="hd-row hd-warn">
          <span className="hd-k">What went wrong</span>
          {last.errorMessage}
        </span>
      ) : null}

      {done && repeatable ? (
        <span className="hd-row hd-quiet">Click to do it again. It will ask you why.</span>
      ) : null}
      {locked ? <span className="hd-row hd-quiet">This cannot be done twice.</span> : null}
    </>
  )

  /* A card with no status is one where "Completed" would be meaningless - editing a
     deal is not a task you finish. Those simply do not carry a status word. */
  const classes = ['ac-card']
  if (status) classes.push('is-' + status)
  if (busy) classes.push('is-busy')

  return (
    <HoverDetail className="ac-hover" detail={popup} suppressed={asking}>
      <div className="ac-shell">
        <button
          type="button"
          className={classes.join(' ')}
          onClick={click}
          disabled={Boolean(busy) || blocked || notRequired || locked}
        >
          <span className="ac-top">
            <span className="ac-title">{title}</span>
            {badge ? <span className="ac-badge">{badge}</span> : null}
            {status ? (
              <span className={'ac-status ac-status-' + status}>{STATUS_WORDS[status]}</span>
            ) : null}
          </span>

          <span className="ac-hint">{hint}</span>

          {/* The fact from the record, which is the thing people actually scan for. */}
          {detail ? (
            <span className="ac-detail">{detail}</span>
          ) : notRequired && blockedReason ? (
            <span className="ac-detail">{blockedReason}</span>
          ) : null}

          {/* When, and who if we know. On the face, because a super admin scanning a
              column of cards should not have to hover each one to find the trail. */}
          {happened && whenText ? (
            <span className={failed ? 'ac-stamp ac-stamp-bad' : 'ac-stamp'}>
              {whenText}
              {last && last.actorName ? ' · ' + last.actorName : ''}
              {count && count > 1 ? ' · ' + count + ' times' : ''}
            </span>
          ) : null}
        </button>

        {extra ? <div className="ac-extra">{extra}</div> : null}

        {asking ? (
          <div className="ac-confirm" role="dialog" aria-label={title + ' again'}>
            <p className="ac-c-head">{title} has already been done.</p>
            <p className="ac-c-when">
              {whenText || 'Time not recorded'}
              {last && last.actorName ? ' · ' + last.actorName : ''}
            </p>

            <label className="ac-c-label" htmlFor={uid + '-why'}>
              Why are you doing it again?
            </label>
            <textarea
              id={uid + '-why'}
              className="ac-c-input"
              value={reason}
              rows={2}
              autoFocus
              placeholder="Seller lost the email, vendor cancelled, wrong details first time"
              onChange={(e) => setReason(e.target.value)}
            />

            <div className="ac-c-actions">
              <button type="button" className="ac-c-cancel" onClick={cancel}>
                Leave it
              </button>
              <button
                type="button"
                className="ac-c-go"
                disabled={reason.trim().length < MIN_REASON}
                onClick={confirm}
              >
                Do it again
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </HoverDetail>
  )
}
